import { create } from 'zustand';
import { useAppStore } from '@indipia/shared';
import { worklogApi } from '../api/worklogApi';
import type {
  BasicInfoPartner,
  BasicInfoSchedule,
  BasicInfoWorklog,
  SiteOption,
} from '../types/worklog.types';

/**
 * 인디공사현황 에이전트 기준정보 임시저장소 (기능명세 메인화면 A1-3 / API명세 §5.3).
 *
 * 에이전트 최상단(WorkLogLayout) 진입 시 GET /api/worklog/basic-info 를 1회 조회해 적재하고,
 * 하위 메뉴(작업요청 목록·등록 등)는 재조회 없이 이 스토어에서 꺼내 쓴다.
 *
 * 정책:
 * - 최초 1회 적재. 공휴일·진행중 현장은 거의 불변이라 자동 무효화(폴링)는 걸지 않는다.
 * - 데이터를 바꾸는 액션(작업요청/현장 등록 등) 성공 후에는 refresh()로 수동 갱신한다.
 * - 로딩/에러 게이팅 UI는 각 하위 화면이 status 를 보고 자율 처리한다(공통 게이팅 없음).
 *
 * ⚠️ appStore 와 동일 주의: useWorklogStore(s => s.somethingDerived(x)) 처럼 매 렌더 새
 *    배열/객체를 반환하는 셀렉터 사용 금지(무한 렌더). 원시 배열을 select 후 컴포넌트에서
 *    useMemo 로 가공하거나 useWorklogStore.getState() 로 읽는다.
 */

type Status = 'idle' | 'loading' | 'ready' | 'error';

/**
 * 공사현황에서 쓰는 **sv-indipia 공통코드 그룹 목록** (API명세 §9.1 `/common/codes`).
 *
 * 플랫폼 init(`appStore.codes`)은 sv-zeus `ZEU_*` 계열만 담고 있어, `E01_*` 등 sv-indipia
 * `TC_CODE_LIST` 코드그룹은 여기에 등록해 별도 조회한다.
 *
 * **새 코드그룹이 필요하면 이 배열에만 추가하면 된다** — 에이전트 진입 시 병렬로 함께 조회돼
 * `codes`에 적재되고, `getCodesByGroup('그룹코드')`로 꺼내 쓸 수 있다.
 *   예) `['E01_SCHEDULE_STATUS', 'SEND_CHANNEL', 'SEND_TYPE']`
 */
export const WORKLOG_CODE_GROUPS: readonly string[] = [
  'E01_SCHEDULE_STATUS', // 공사진행상태 — 준비중/시공중/시공완료/지연
  'A02_FIELD_ISSUE_TYPE', // 현장보고 이슈유형(REQ-2-04, 작업현황 등록 화면 이슈항목)
];

/** 공통코드 항목 — §9.1 응답(`items[].codeValue`/`name`)을 플랫폼 형태로 정규화한 것 */
export interface WorklogCode {
  codeGroupCode: string;
  codeValue: string;
  codeName: string;
}

interface WorklogState {
  status: Status;
  worklogs: BasicInfoWorklog[];
  /** 작업요청 작성 현장 셀렉박스 목록 — worklog 있는 현장 + site만 있는 현장(완료 제외), BE 정렬 순서 유지 */
  siteOptions: SiteOption[];
  partners: BasicInfoPartner[];
  /** 현장보고 작성자 필터 후보(시공+자재 파트너) — 서버가 안 주면 partners 로 대체 */
  reportWriters: BasicInfoPartner[];
  schedules: BasicInfoSchedule[];
  /**
   * WORKLOG_CODE_GROUPS 전 그룹의 공통코드 — §9.1 별도 조회분(그룹 구분 없이 한 배열).
   * 조회 실패한 그룹은 항목이 없으며, 화면은 각자의 폴백 라벨로 동작한다
   * (기준정보 전체를 error로 떨어뜨리지 않는다).
   */
  codes: WorklogCode[];
  todayIsHoliday: boolean;
  holidayName: string | null;
  /** 적재 시각(epoch ms) — 디버깅·향후 staleness 판단용 */
  loadedAt: number | null;
  error: unknown;

  /** 기준정보 조회. status가 ready/loading이면 재호출하지 않음(force=true면 강제). */
  load: (force?: boolean) => Promise<void>;
  /** 강제 재조회 — 데이터 변경 액션 성공 후 호출. */
  refresh: () => Promise<void>;
  /** 초기화 — 에이전트 이탈·로그아웃 시. */
  reset: () => void;
  /**
   * 코드그룹별 공통코드 조회 — 서버가 내려준 순서를 유지한다.
   * ⚠️ 매 렌더 새 배열을 만들므로 **셀렉터로 직접 쓰지 말 것**(무한 렌더).
   *    `useWorklogStore.getState().getCodesByGroup(g)` 또는 `s.codes`를 select 후 useMemo로 가공.
   */
  getCodesByGroup: (codeGroupCode: string) => WorklogCode[];
}

const initialData = {
  status: 'idle' as Status,
  worklogs: [] as BasicInfoWorklog[],
  siteOptions: [] as SiteOption[],
  partners: [] as BasicInfoPartner[],
  reportWriters: [] as BasicInfoPartner[],
  schedules: [] as BasicInfoSchedule[],
  codes: [] as WorklogCode[],
  todayIsHoliday: false,
  holidayName: null as string | null,
  loadedAt: null as number | null,
  error: null as unknown,
};

// 동시 호출 합류(dedup) — 진행 중 요청 1개만 유지해 중복 네트워크를 막는다.
let inFlight: Promise<void> | null = null;

export const useWorklogStore = create<WorklogState>()((set, get) => ({
  ...initialData,

  load: (force = false) => {
    const { status } = get();
    // 이미 적재됐거나 조회 중이고 강제 아님 → skip(진행 중이면 그 Promise에 합류)
    if (!force && (status === 'ready' || status === 'loading')) {
      return inFlight ?? Promise.resolve();
    }

    set({ status: 'loading', error: null });

    // 구독 에이전트 번호(인디플래너=8 포함 시에만 BE가 스케줄 조회). appStore에서 비반응형으로 읽음.
    const subscriptIds = useAppStore.getState().subscriptions.map((s) => s.agentId);

    // 공통코드(§9.1)는 basic-info와 병렬 조회한다 — 서로 의존하지 않는다.
    // 그룹별로 개별 catch를 둬, 한 그룹이 실패해도 나머지 그룹과 기준정보는 정상 적재된다.
    // (여기서 throw하면 현장·반장 목록까지 못 쓰게 되고, 화면은 폴백 라벨로 충분히 동작한다.)
    const codesPromise = Promise.all(
      WORKLOG_CODE_GROUPS.map((codeGroupCode) =>
        worklogApi
          .getCommonCodes(codeGroupCode)
          .then((res) => {
            // 응답 data는 코드 배열 그대로(codeGroupCode/code/codeName/codeOrder).
            const items = res.data.data;
            if (!items?.length && import.meta.env.DEV) {
              // 응답은 왔는데 비었다 = BE에 데이터가 없거나 응답 형태가 다르다.
              // 조용히 [] 로 떨어지면 원인 파악이 어려워 개발모드에서만 실제 응답을 찍는다.
              console.warn(`[worklogStore] 공통코드 '${codeGroupCode}' 결과 없음. 응답:`, res.data);
            }
            return (items ?? []).map((it) => ({
              codeGroupCode: it.codeGroupCode ?? codeGroupCode,
              codeValue: it.codeValue,
              codeName: it.codeName,
            }));
          })
          .catch((err) => {
            if (import.meta.env.DEV) {
              console.warn(
                `[worklogStore] 공통코드 '${codeGroupCode}' 조회 실패 —`,
                err?.response?.status ?? err?.code ?? err,
                err?.response?.data ?? '',
              );
            }
            return [] as WorklogCode[];
          }),
      ),
    ).then((lists) => lists.flat());

    inFlight = Promise.all([worklogApi.getBasicInfo(subscriptIds), codesPromise])
      .then(([res, codes]) => {
        const d = res.data.data;
        set({
          status: 'ready',
          worklogs: d.worklogs ?? [],
          siteOptions: d.siteOptions ?? [],
          partners: d.partners ?? [],
          reportWriters: d.reportWriters ?? d.partners ?? [],
          schedules: d.schedules ?? [],
          codes,
          todayIsHoliday: d.todayIsHoliday ?? false,
          holidayName: d.holidayName ? d.holidayName : null, // BE의 "" → null 정규화
          loadedAt: Date.now(),
          error: null,
        });
      })
      .catch((err) => {
        set({ status: 'error', error: err });
      })
      .finally(() => {
        inFlight = null;
      });

    return inFlight;
  },

  refresh: () => get().load(true),

  reset: () => {
    inFlight = null;
    set({ ...initialData });
  },

  getCodesByGroup: (codeGroupCode) =>
    get().codes.filter((c) => c.codeGroupCode === codeGroupCode),
}));

// [임시·개발전용] DevTools 콘솔에서 store 확인용. 검증 끝나면 이 블록 삭제.
//   window.worklogStore.getState()  → 현재 값
if (import.meta.env.DEV) {
  (window as unknown as { worklogStore: typeof useWorklogStore }).worklogStore =
    useWorklogStore;
}
